import { useMemo } from 'react';

import { buildIntercomBootProps, type IntercomBootProps, type IntercomBootUser } from '../utils/intercom-boot-props';

export type AnonymousIntercomBootProps = { hideDefaultLauncher: boolean };

/**
 * Routes where the launcher bubble stays hidden whoever the visitor is, logged in or not.
 *
 * The /getting-started wizard keeps its «Continua» button pinned to the bottom of the screen, and
 * on a phone the bubble sat right on top of it. The Messenger itself stays available: step 1 opens
 * it programmatically when the venue is not on Google Maps.
 *
 * This is deliberately not part of the authenticated boot props (see below): the caller applies it
 * with an `update`, which toggles the launcher without booting the Messenger again.
 */
export const isLauncherHiddenOnRoute = (pathname: string): boolean => pathname.startsWith('/getting-started');

/**
 * The props handed to the Intercom Messenger `boot` call, memoised so the object keeps its
 * identity for as long as nothing that matters has changed.
 *
 * The identity is the point, not an optimisation. The caller tears the Messenger down and boots
 * it again whenever this value changes, and for a logged-in customer the boot now carries a JWT
 * that expires after 24 hours and is frozen for the life of the SPA: `user` is fetched once by
 * AuthProvider.initialize and never refetched. Recomputing this on every navigation, which is
 * what a `pathname` dependency did, meant a tab left open past that point re-booted the Messenger
 * with an expired token and Intercom refused it. While the boot carried a userHash that never
 * expired the same churn was merely wasteful, which is why it went unnoticed.
 *
 * So the authenticated value depends on the user alone. The anonymous one does depend on where
 * the visitor is, but only through a single boolean, so moving between two pages that agree on it
 * does not churn the Messenger either.
 */
export const useIntercomBootProps = (
  user: IntercomBootUser | null | undefined,
  isEmployerApp: boolean,
  pathname: string
): IntercomBootProps | AnonymousIntercomBootProps => {
  const authenticated = useMemo(
    () => (user ? buildIntercomBootProps(user, isEmployerApp) : undefined),
    [isEmployerApp, user]
  );

  const hideLauncherForAnonymous =
    pathname.includes('/locale') || pathname.includes('/worker/file-complaint') || isLauncherHiddenOnRoute(pathname);

  const anonymous = useMemo(() => ({ hideDefaultLauncher: hideLauncherForAnonymous }), [hideLauncherForAnonymous]);

  return authenticated ?? anonymous;
};
